/**
 * Adaptive Animation Hook
 *
 * Provides optimized animation configurations based on device capabilities
 * and performance characteristics. Automatically adapts to different devices
 * and connection qualities for optimal user experience.
 *
 * @example
 * const { getAnimationProps, getStaggerProps, shouldAnimate } = useAdaptiveAnimation();
 */

import { useMemo } from 'react';
import { useDeviceCapabilities, AnimationConfig } from './useDeviceCapabilities';
import { Transition } from 'motion/react';

export interface AnimationProps {
  initial?: any;
  animate?: any;
  exit?: any;
  transition?: Transition;
  whileHover?: any;
  whileTap?: any;
  whileInView?: any;
  viewport?: {
    once?: boolean;
    margin?: string;
    amount?: number | 'some' | 'all';
  };
}

export interface StaggerProps {
  staggerChildren?: number;
  delayChildren?: number;
}

export const useAdaptiveAnimation = (): {
  getAnimationProps: (type?: 'fadeIn' | 'slideUp' | 'slideLeft' | 'scale' | 'bounce', customDuration?: number, customDelay?: number) => {
    initial?: any;
    animate?: any;
    exit?: any;
    transition?: Transition;
  };
  getStaggerProps: (customStagger?: number) => StaggerProps;
  getViewportProps: (customMargin?: string, customAmount?: number) => AnimationProps['viewport'];
  getLazyAnimationProps: () => {
    initial?: any;
    animate?: any;
    exit?: any;
    transition?: Transition;
    whileInView?: any;
    viewport?: {
      once?: boolean;
      margin?: string;
      amount?: number | 'some' | 'all';
    };
  };
  getHoverProps: (scale?: number, duration?: number) => {
    whileHover?: any;
    whileTap?: any;
  };
  shouldAnimate: boolean;
  deviceTier: 'high-end' | 'mid-range' | 'low-end' | 'mobile';
  isMobile: boolean;
  animationConfig: AnimationConfig;
  reducedMotion: boolean;
} => {
  // Get device capabilities at the hook level (not inside callbacks)
  let deviceTier: 'high-end' | 'mid-range' | 'low-end' | 'mobile' = 'mid-range';
  let reducedMotion = false;
  let isMobile = false;
  let animationConfig: AnimationConfig;

  try {
    const capabilities = useDeviceCapabilities();
    deviceTier = capabilities.deviceTier || 'mid-range';
    animationConfig = capabilities.animationConfig || {
      duration: { fast: 0.2, normal: 0.5, slow: 0.8 },
      stagger: { fast: 0.05, normal: 0.1, slow: 0.15 },
      easing: {
        smooth: [0.25, 0.46, 0.45, 0.94],
        bounce: [0.68, -0.55, 0.265, 1.55],
        sharp: [0.4, 0, 0.2, 1],
      },
      enableLazyAnimations: true,
      useHardwareAcceleration: true,
      reducedMotion: false,
    };
    reducedMotion = capabilities.reducedMotion || false;
    isMobile = capabilities.isMobile || false;
  } catch (error) {
    console.warn('useAdaptiveAnimation: Error in useDeviceCapabilities, using defaults:', error);
    animationConfig = {
      duration: { fast: 0.2, normal: 0.5, slow: 0.8 },
      stagger: { fast: 0.05, normal: 0.1, slow: 0.15 },
      easing: {
        smooth: [0.25, 0.46, 0.45, 0.94],
        bounce: [0.68, -0.55, 0.265, 1.55],
        sharp: [0.4, 0, 0.2, 1],
      },
      enableLazyAnimations: true,
      useHardwareAcceleration: true,
      reducedMotion: false,
    };
  }

  /**
   * Get optimized animation properties for a component
   */
  const getAnimationProps = useMemo(() => {
    return (
      type: 'fadeIn' | 'slideUp' | 'slideLeft' | 'scale' | 'bounce' = 'fadeIn',
      customDuration?: number,
      customDelay?: number
    ): AnimationProps => {
      try {
        if (reducedMotion) {
          return {
            initial: { opacity: 0 },
            animate: { opacity: 1 },
            transition: { duration: 0.01 }
          };
        }

        const duration = customDuration || animationConfig.duration?.normal || 0.5;
        const delay = customDelay || 0;

      const baseTransition = {
        duration,
        delay,
        ease: animationConfig.easing.smooth,
      };

      const variants: Record<string, AnimationProps> = {
        fadeIn: {
          initial: { opacity: 0 },
          animate: { opacity: 1 },
          transition: baseTransition,
        },
        slideUp: {
          initial: { opacity: 0, y: 30 },
          animate: { opacity: 1, y: 0 },
          transition: baseTransition,
        },
        slideLeft: {
          initial: { opacity: 0, x: -30 },
          animate: { opacity: 1, x: 0 },
          transition: baseTransition,
        },
        scale: {
          initial: { opacity: 0, scale: 0.9 },
          animate: { opacity: 1, scale: 1 },
          transition: baseTransition,
        },
        bounce: {
          initial: { opacity: 0, y: 50 },
          animate: { opacity: 1, y: 0 },
          transition: {
            ...baseTransition,
            ease: animationConfig.easing.bounce,
          },
        },
      };

      const variant = variants[type];

      // Add hardware acceleration for better performance
      if (animationConfig.useHardwareAcceleration && !isMobile) {
        // Add transform3d to the DOM element via CSS class instead
        // This will be handled by the CSS classes in globals.css
      }

        // Return variant with only compatible properties for motion components
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { whileHover: _whileHover, whileTap: _whileTap, viewport: _viewport, ...safeVariant } = variant;
        return safeVariant;
      } catch (error) {
        console.warn('useAdaptiveAnimation: Error in getAnimationProps, using safe defaults:', error);
        const safeDefaults = {
          initial: { opacity: 0 },
          animate: { opacity: 1 },
          transition: { duration: 0.3 }
        };
        // Return safe defaults with only compatible properties for motion components
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { whileHover: _whileHover, whileTap: _whileTap, viewport: _viewport, ...safeProps } = safeDefaults as AnimationProps;
        return safeProps;
      }
    };
  }, [animationConfig, reducedMotion, isMobile]);

  /**
   * Get optimized stagger properties for container components
   */
  const getStaggerProps = useMemo(() => {
    return (customStagger?: number): StaggerProps => {
      try {
        if (reducedMotion) {
          return {};
        }

        const staggerDelay = customStagger || animationConfig.stagger?.normal || 0.1;

        return {
          staggerChildren: staggerDelay,
          delayChildren: 0,
        };
      } catch (error) {
        console.warn('useAdaptiveAnimation: Error in getStaggerProps, using safe defaults:', error);
        return {};
      }
    };
  }, [animationConfig, reducedMotion]);

  /**
   * Get viewport intersection properties optimized for device
   */
  const getViewportProps = useMemo(() => {
    return (customMargin?: string, customAmount?: number): AnimationProps['viewport'] => {
      try {
        const margin = customMargin || (isMobile ? "-30px" : "-100px");
        const amount = customAmount || (isMobile ? 0.1 : 0.3);

        return {
          once: true,
          margin,
          amount,
        };
      } catch (error) {
        console.warn('useAdaptiveAnimation: Error in getViewportProps, using safe defaults:', error);
        return {
          once: true,
          margin: "-50px",
          amount: 0.1,
        };
      }
    };
  }, [isMobile]);

  /**
   * Check if animations should be enabled based on device and preferences
   */
  const shouldAnimate = useMemo(() => {
    return !reducedMotion && deviceTier !== 'low-end';
  }, [reducedMotion, deviceTier]);

  /**
   * Get lazy loading configuration for animations
   */
  const getLazyAnimationProps = useMemo(() => {
    return (): AnimationProps => {
      try {
        if (!animationConfig.enableLazyAnimations) {
          return {};
        }

        const animationProps = {
          initial: { opacity: 0 },
          whileInView: { opacity: 1 },
          viewport: getViewportProps(),
          transition: {
            duration: animationConfig.duration?.fast || 0.3,
            ease: animationConfig.easing?.smooth || [0.25, 0.46, 0.45, 0.94],
          },
        };

        // Return animation props with only compatible properties for motion components
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { whileHover: _whileHover, whileTap: _whileTap, ...safeProps } = animationProps as AnimationProps;
        return safeProps;
      } catch (error) {
        console.warn('useAdaptiveAnimation: Error in getLazyAnimationProps, using safe defaults:', error);
        const safeDefaults = {
          initial: { opacity: 0 },
          whileInView: { opacity: 1 },
          transition: { duration: 0.3 },
        };
        // Return safe defaults with only compatible properties for motion components
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { whileHover: _whileHover, whileTap: _whileTap, ...safeProps } = safeDefaults as AnimationProps;
        return safeProps;
      }
    };
  }, [animationConfig, getViewportProps]);

  /**
   * Get hover animation properties optimized for touch devices
   */
  const getHoverProps = useMemo(() => {
    return (scale: number = 1.05, duration?: number): Pick<AnimationProps, 'whileHover' | 'whileTap'> => {
      try {
        if (isMobile) {
          // Disable hover effects on touch devices to prevent stuck states
          return {};
        }

        return {
          whileHover: {
            scale,
            transition: {
              duration: duration || animationConfig.duration?.fast || 0.2,
              ease: animationConfig.easing?.smooth || [0.25, 0.46, 0.45, 0.94],
            },
          },
          whileTap: {
            scale: scale * 0.95,
            transition: {
              duration: (animationConfig.duration?.fast || 0.2) * 0.5,
              ease: animationConfig.easing?.sharp || [0.4, 0, 0.2, 1],
            },
          },
        };
      } catch (error) {
        console.warn('useAdaptiveAnimation: Error in getHoverProps, using safe defaults:', error);
        return {};
      }
    };
  }, [isMobile, animationConfig]);

  return {
    getAnimationProps,
    getStaggerProps,
    getViewportProps,
    getLazyAnimationProps,
    getHoverProps,
    shouldAnimate,
    deviceTier,
    isMobile,
    animationConfig,
    reducedMotion,
  };
};

export default useAdaptiveAnimation;
